import type { PolyFeeSchedule, PolyRelayPosition } from '@/lib/polymarket/relay';
import { estimatePolyOrderFeeCents } from '@/lib/pricing/polyFees';

export interface UnrealizedCoverage {
  /** Complete account total. Null means at least one position is unpriced. */
  valueCents: number | null;
  /** Diagnostic subtotal over positions with every required accounting input. */
  pricedValueCents: number;
  pricedPositions: number;
  totalPositions: number;
  complete: boolean;
}

/** Turn per-position values into an honest account total. A null row means a
 * mark, fee schedule, or fee allocation is unknown; never silently turn that
 * row into zero and present the remaining subtotal as complete. */
export function summarizeUnrealizedCoverage(
  values: ReadonlyArray<number | null>,
): UnrealizedCoverage {
  let pricedValueCents = 0;
  let pricedPositions = 0;
  for (const value of values) {
    if (value == null || !Number.isFinite(value)) continue;
    pricedValueCents += value;
    pricedPositions += 1;
  }
  const totalPositions = values.length;
  const complete = pricedPositions === totalPositions;
  return {
    valueCents: complete ? pricedValueCents : null,
    pricedValueCents,
    pricedPositions,
    totalPositions,
    complete,
  };
}

/** Fee-aware Polymarket unrealized P&L with explicit coverage. A position is
 * priced only when its live mark, open-share fee allocation, and close-fee
 * schedule are all known. */
export function computePolyUnrealizedCoverage(args: {
  positions: ReadonlyArray<PolyRelayPosition>;
  schedules: ReadonlyMap<string, PolyFeeSchedule | null>;
  getNowCents: (position: PolyRelayPosition) => number | null;
}): UnrealizedCoverage {
  const { positions, schedules, getNowCents } = args;
  return summarizeUnrealizedCoverage(
    positions.map((position) => {
      const nowCents = getNowCents(position);
      if (nowCents == null || position.openFeesCents == null) return null;
      const closeFeeCents = estimatePolyOrderFeeCents({
        priceCents: nowCents,
        count: position.netCount,
        role: 'taker',
        schedule: schedules.get(position.ticker),
      });
      if (closeFeeCents == null) return null;
      const entryCostCents =
        position.costBasisCents ?? position.avgEntryPriceCents * position.netCount;
      return (
        nowCents * position.netCount -
        closeFeeCents -
        entryCostCents -
        position.openFeesCents
      );
    }),
  );
}

/** Why one open position contributes no unrealized value. Each reason names a
 * concrete missing input so a withheld headline can say what is missing
 * instead of a bare count. The account total stays withheld regardless. */
export type UnpricedReason =
  | 'no-mark'
  | 'no-basis'
  | 'fee-loading'
  | 'fee-error'
  | 'fee-missing'
  | 'fee-unsupported'
  | 'poly-fee-allocation'
  | 'poly-fee-schedule'
  | 'us-entry-basis'
  | 'us-close-fee';

const UNPRICED_REASON_TEXT: Record<UnpricedReason, [singular: string, plural: string]> = {
  'us-entry-basis': ['without complete Polymarket US entry cost and fees', 'without complete Polymarket US entry cost and fees'],
  'us-close-fee': ['without a Polymarket US closing-fee estimate', 'without a Polymarket US closing-fee estimate'],
  'no-mark': ['without a live mark', 'without a live mark'],
  'no-basis': ['without an entry basis', 'without an entry basis'],
  'fee-loading': ['waiting for the Kalshi fee schedule to load', 'waiting for the Kalshi fee schedule to load'],
  'fee-error': ['without an available fee schedule (refresh failed)', 'without an available fee schedule (refresh failed)'],
  'fee-missing': ['in a series without a usable closing-fee rate', 'in series without a usable closing-fee rate'],
  'fee-unsupported': ['in a flat-fee series (closing fee not estimable)', 'in flat-fee series (closing fee not estimable)'],
  'poly-fee-allocation': ['without a Polymarket open-fee allocation', 'without a Polymarket open-fee allocation'],
  'poly-fee-schedule': ['without a Polymarket fee schedule', 'without a Polymarket fee schedule'],
};

const UNPRICED_REASON_ORDER: readonly UnpricedReason[] = [
  'no-mark', 'no-basis', 'fee-loading', 'fee-error', 'fee-missing', 'fee-unsupported',
  'poly-fee-allocation', 'poly-fee-schedule', 'us-entry-basis', 'us-close-fee',
];

/** "2 without a live mark; 1 in a series without a usable closing-fee rate".
 * Null when nothing is unpriced. Order is fixed so the text is stable. */
export function describeUnpricedReasons(
  reasons: ReadonlyArray<UnpricedReason | null | undefined>,
): string | null {
  const counts = new Map<UnpricedReason, number>();
  for (const reason of reasons) {
    if (!reason) continue;
    counts.set(reason, (counts.get(reason) ?? 0) + 1);
  }
  if (counts.size === 0) return null;
  return UNPRICED_REASON_ORDER
    .filter((reason) => counts.has(reason))
    .map((reason) => {
      const n = counts.get(reason)!;
      return `${n} ${UNPRICED_REASON_TEXT[reason][n === 1 ? 0 : 1]}`;
    })
    .join('; ');
}

/** Tooltip sentence for a withheld total: the priced subtotal is named as a
 * subtotal, never as the account total, and every unpriced reason follows.
 * `formatCents` is the caller's own money formatter so surfaces stay
 * consistent with their rows. */
export function describeWithheldTotal(
  coverage: Pick<UnrealizedCoverage, 'pricedValueCents' | 'pricedPositions' | 'totalPositions'>,
  unpricedSummary: string | null,
  formatCents: (cents: number) => string,
): string {
  const head = `${coverage.pricedPositions}/${coverage.totalPositions} positions priced`;
  const subtotal = coverage.pricedPositions > 0
    ? `; priced subtotal ${formatCents(coverage.pricedValueCents)} is not the account total`
    : '';
  const why = unpricedSummary ? `. Unpriced: ${unpricedSummary}` : '';
  return `${head}${subtotal}${why}`;
}

/** Visible headline text while the total is withheld: the subtotal, labelled
 * as one, only when at least one position is priced; a bare count otherwise
 * (a "$0.00" over zero priced positions would read as a real total). */
export function withheldHeadlineText(
  coverage: Pick<UnrealizedCoverage, 'pricedValueCents' | 'pricedPositions' | 'totalPositions'>,
  formatCents: (cents: number) => string,
): string {
  const count = `${coverage.pricedPositions}/${coverage.totalPositions}`;
  return coverage.pricedPositions > 0
    ? `${formatCents(coverage.pricedValueCents)} · subtotal ${count}`
    : `— (${count})`;
}
